import type { CheckoutEventName, CheckoutEventListener } from '../types';

/**
 * Type-safe EventEmitter for the checkout system
 */
export class EventEmitter {
  private listeners: Map<CheckoutEventName, Set<CheckoutEventListener<any>>> = new Map();

  /**
   * Add an event listener
   */
  on<T extends CheckoutEventName>(
    event: T,
    listener: CheckoutEventListener<T>
  ): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener);
    return this;
  }

  /**
   * Add a one-time event listener
   */
  once<T extends CheckoutEventName>(
    event: T,
    listener: CheckoutEventListener<T>
  ): this {
    // @ts-ignore - Complex type inference issue with spread arguments
    const onceListener = ((...args: any[]) => {
      this.off(event, onceListener);
      (listener as any)(...args);
    }) as CheckoutEventListener<T>;
    
    return this.on(event, onceListener);
  }

  /**
   * Remove an event listener
   */
  off<T extends CheckoutEventName>(
    event: T,
    listener: CheckoutEventListener<T>
  ): this {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(listener);
      if (eventListeners.size === 0) {
        this.listeners.delete(event);
      }
    }
    return this;
  }

  /**
   * Remove all listeners for a specific event
   */
  offAll(event: CheckoutEventName): this {
    this.listeners.delete(event);
    return this;
  }

  /**
   * Remove all event listeners
   */
  removeAllListeners(): this {
    this.listeners.clear();
    return this;
  }

  /**
   * Emit an event
   */
  emit<T extends CheckoutEventName>(
    event: T,
    ...args: any[]
  ): boolean {
    const eventListeners = this.listeners.get(event);
    if (!eventListeners || eventListeners.size === 0) {
      return false;
    }

    const listeners = Array.from(eventListeners);
    listeners.forEach((listener) => {
      try {
        (listener as any)(...args);
      } catch (error) {
        console.error(`Error in event listener for ${event}:`, error);
        // Emit error event if available
        if (event !== 'checkout:error') {
          this.emit('checkout:error', error as any);
        }
      }
    });

    return true;
  }

  /**
   * Get the number of listeners for a specific event
   */
  listenerCount(event: CheckoutEventName): number {
    const eventListeners = this.listeners.get(event);
    return eventListeners ? eventListeners.size : 0;
  }

  /**
   * Get all event names that have listeners
   */
  eventNames(): CheckoutEventName[] {
    return Array.from(this.listeners.keys());
  }

  /**
   * Get all listeners for a specific event
   */
  getListeners<T extends CheckoutEventName>(event: T): CheckoutEventListener<T>[] {
    const eventListeners = this.listeners.get(event);
    return eventListeners ? Array.from(eventListeners) : [];
  }

  /**
   * Check if there are any listeners for a specific event
   */
  hasListeners(event: CheckoutEventName): boolean {
    return this.listenerCount(event) > 0;
  }
} 